// Both cart surfaces use the same replacement transaction and rendered totals.
const lineIdentity = (item, variantId = item.variant_id || item.id) => JSON.stringify([
  Number(variantId),
  Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
  item.selling_plan_allocation?.selling_plan?.id || null,
]);

export async function replaceCartLine(request, key, variantId, messages) {
  const before = await request('cart.js');
  const original = before.items.find((item) => item.key === key);
  if (!original) throw new Error(messages.changed);
  if (Number(original.variant_id || original.id) === variantId) return before;

  const originalIdentity = lineIdentity(original);
  const targetIdentity = lineIdentity(original, variantId);
  const findLine = (cart, identity) => {
    const matches = cart.items.filter((item) => lineIdentity(item) === identity);
    if (matches.length > 1) throw new Error(messages.review);
    return matches[0];
  };
  const previousQuantity = findLine(before, targetIdentity)?.quantity || 0;
  const wantedQuantity = previousQuantity + original.quantity;
  const replacement = { id: variantId, quantity: original.quantity, properties: original.properties || {} };
  if (original.selling_plan_allocation) replacement.selling_plan = original.selling_plan_allocation.selling_plan.id;

  try {
    await request('cart/add.js', { items: [replacement] });
    // Automatic discounts can change line keys; resolve the current key again.
    const addedCart = await request('cart.js');
    const current = findLine(addedCart, originalIdentity);
    const target = findLine(addedCart, targetIdentity);
    if (!current || current.quantity !== original.quantity || target?.quantity !== wantedQuantity) {
      throw new Error(messages.changed);
    }
    return await request('cart/change.js', { id: current.key, quantity: 0 });
  } catch (error) {
    // A failed HTTP response can still mean a cart mutation was committed.
    // Re-read before rolling back, so a completed replacement is never removed.
    let latest;
    try {
      latest = await request('cart.js');
      const current = findLine(latest, originalIdentity);
      const target = findLine(latest, targetIdentity);
      const targetQuantity = target?.quantity || 0;
      if (!current && targetQuantity === wantedQuantity) return latest;
      if (current?.quantity !== original.quantity) throw new Error(messages.review);
      if (targetQuantity > previousQuantity && targetQuantity <= wantedQuantity) {
        try {
          latest = await request('cart/change.js', { id: target.key, quantity: previousQuantity });
        } catch (rollbackError) {
          latest = await request('cart.js');
        }
      }
      if (findLine(latest, originalIdentity)?.quantity !== original.quantity ||
          (findLine(latest, targetIdentity)?.quantity || 0) !== previousQuantity) {
        throw new Error(messages.review);
      }
    } catch (reviewError) {
      const failure = new Error(messages.review);
      failure.cart = latest;
      throw failure;
    }
    error.cart = latest;
    throw error;
  }
}

async function renderCart(cart, request) {
  const sections = ['cart-drawer', 'cart-template', 'cart-count'];
  const url = new URL(window.location.href);
  url.searchParams.set('sections', sections.join(','));
  const html = await request(url.pathname + url.search, undefined, true);
  const parser = new DOMParser();
  const targets = [
    ['cart-drawer', '#MinimogCartDrawerHeader'],
    ['cart-template', '#MinimogCartHeader'],
    ['cart-template', '#MinimogCartBody'],
    ['cart-template', '#MinimogCartFooter'],
  ];
  // Validate every fragment before changing the displayed cart.
  const replacements = targets.flatMap(([section, selector]) => {
    const target = document.querySelector(selector);
    if (!target) return [];
    const source = parser.parseFromString(html[section] || '', 'text/html').querySelector(selector);
    if (!source) throw new Error('Cart section unavailable');
    return [{ target, source }];
  });
  const countDocument = parser.parseFromString(html['cart-count'] || '', 'text/html');
  if (!countDocument.querySelector('.m-cart-count')) throw new Error('Cart count unavailable');
  replacements.forEach(({ target, source }) => { target.innerHTML = source.innerHTML; });
  document.querySelectorAll('m-cart-count').forEach((element) => element.onUpdate(countDocument));
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
}

if (typeof document !== 'undefined') {
  let updating = false;
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-arte-apply-variant]');
    if (!button || button.disabled || updating) return;
    event.preventDefault();
    const box = button.closest('[data-arte-cart-edit]');
    const select = box.querySelector('select');
    const variantId = Number(select.value);
    if (!variantId || select.selectedOptions[0]?.disabled) return;
    if (variantId === Number(box.dataset.original)) {
      box.open = false;
      box.querySelector('summary').focus();
      return;
    }
    const messages = {
      error: box.dataset.error, changed: box.dataset.changed, review: box.dataset.review,
      updating: box.dataset.updating, updated: box.dataset.updated,
    };
    const request = async (endpoint, body, absolute = false) => {
      const response = await fetch(absolute ? endpoint : window.Shopify.routes.root + endpoint, {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(20000),
        headers: { Accept: absolute ? 'text/html' : 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const result = await response.json();
      if (!response.ok || result.status >= 400) {
        throw new Error(result.description || result.message || messages.error);
      }
      return result;
    };
    let announcer = document.getElementById('ArteCartUpdateStatus');
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.id = 'ArteCartUpdateStatus';
      announcer.className = 'a-visually-hidden';
      announcer.setAttribute('role', 'status');
      announcer.setAttribute('aria-live', 'polite');
      document.body.append(announcer);
    }
    const surfaceId = box.closest('#MinimogCartDrawer') ? 'MinimogCartDrawer' : 'MinimogCart';
    const surfaces = [...document.querySelectorAll('#MinimogCartDrawer, #MinimogCart')];
    const previousInert = surfaces.map((surface) => surface.inert);
    const status = box.querySelector('[role="status"]');
    updating = true;
    button.disabled = true;
    status.textContent = messages.updating;
    announcer.textContent = messages.updating;
    surfaces.forEach((surface) => { surface.inert = true; surface.setAttribute('aria-busy', 'true'); });
    let cart, errorMessage;
    try {
      cart = await replaceCartLine(request, box.dataset.key, variantId, messages);
    } catch (error) {
      cart = error.cart;
      errorMessage = error.name === 'TypeError' || error.name === 'TimeoutError' ? messages.error : error.message;
    }
    try {
      if (cart) await renderCart(cart, request);
    } catch (renderError) {
      // A full reload is the safe fallback after a successful server-side update.
      window.location.reload();
      return;
    } finally {
      surfaces.forEach((surface, index) => { surface.inert = previousInert[index]; surface.removeAttribute('aria-busy'); });
      button.disabled = false;
      updating = false;
    }
    const surface = document.getElementById(surfaceId);
    const newBox = [...(surface?.querySelectorAll('[data-arte-cart-edit]') || [])].find((element) =>
      errorMessage ? (element.dataset.key === box.dataset.key || element.dataset.original === box.dataset.original) : Number(element.dataset.original) === variantId
    ) || (box.isConnected ? box : null);
    const message = errorMessage || messages.updated;
    announcer.textContent = message;
    if (newBox) {
      newBox.open = Boolean(errorMessage);
      newBox.querySelector('[role="status"]').textContent = message;
      newBox.querySelector('summary').focus({ preventScroll: true });
    }
  });
}
